import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationDetailsService } from '../../loan-applications/loan-application-details/loan-application-details.service';
import { ApplicationDetailsDTO } from '../../loan-applications/loan-application-details/loan-application-details.interface';
import { LoanApplicationItem } from '../../loan-applications/loan-applications.interface';
import { ToastrService } from 'ngx-toastr';
import { LosStateService } from '../los-state.service';

@Component({
  selector: 'app-credit-decision',
  templateUrl: './credit-decision.component.html',
  styleUrls: ['./credit-decision.component.css']
})
export class CreditDecisionComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private applicationDetailsService = inject(ApplicationDetailsService);
  private losState                   = inject(LosStateService);
  private toastr                     = inject(ToastrService);
  private destroyRef                 = inject(DestroyRef);

  // ─── State ────────────────────────────────────────────────────────────────
  applicationData:    LoanApplicationItem | null = null;
  applicationDetails: ApplicationDetailsDTO[]    = [];
  isLoading     = false;
  detailsLoaded = false;

  // ─── Bureau fields (read from ApplicationDetails) ─────────────────────────
  creditScore:  string = '—';
  riskGrade:    string = '—';
  activeLoans:  string = '—';
  closedLoans:  string = '—';
  worstDpd:     string = '—';
  decisionFlag: string = '—';
  enquiries3m:  string = '—';
  enquiries6m:  string = '—';
  writeOffFlag: string = '0';

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => this.applicationData = app);

    // Primary source: reactive state stream (already fetched by parent/LosState)
    this.losState.applicationDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        if (details?.length) {
          this.applicationDetails = details;
          this.mapBureauFields(details);
          this.detailsLoaded = true;
        }
      });

    // Fallback: direct fetch in case state hasn't hydrated yet
    if (!this.detailsLoaded) {
      this.fetchDecisionData();
    }
  }

  // ─── Direct fetch (fallback) ──────────────────────────────────────────────
  fetchDecisionData(): void {
    if (!this.applicationId) return;
    this.isLoading = true;

    this.applicationDetailsService
      .ApplicationDetails_ReadByApplicationId({ applicationId: this.applicationId })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res?.items?.length) {
            this.applicationDetails = res.items;
            this.mapBureauFields(res.items);
            this.detailsLoaded = true;
          } else {
            this.toastr.info('No credit bureau data available yet.');
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.toastr.error('Failed to fetch credit decision data');
          console.error('[CreditDecision] fetch error:', err);
        }
      });
  }

  // ─── Map bureau parameter names → component fields ────────────────────────
  private mapBureauFields(items: ApplicationDetailsDTO[]): void {
    const find = (name: string) =>
      items.find(i => i.parameterName === name)?.value || '—';

    this.creditScore  = find('Credit_Score');
    this.riskGrade    = find('Risk_Grade');
    this.activeLoans  = find('Active_Loans');
    this.closedLoans  = find('Closed_Loans');
    this.worstDpd     = find('Worst_DPD');
    this.decisionFlag = find('Decision_Flag');
    this.enquiries3m  = find('Enquiries_3M');
    this.enquiries6m  = find('Enquiries_6M');
    this.writeOffFlag = find('Write_Off_Flag') === '1' ? '1' : '0';
  }

  // ─── Bank Statement parameter accessor ───────────────────────────────────
  /**
   * Read a persisted Bank-Statement parameter from applicationDetails.
   * Mirrors the same method in BureauAaComponent so the template
   * can access any BS field with zero duplication.
   */
  getBSValue(parameterName: string, defaultValue: string = ''): string {
    if (!this.applicationDetails?.length) return defaultValue;
    const match = this.applicationDetails.find(
      d => d.parameterName?.toLowerCase() === parameterName.toLowerCase()
    );
    const val = match?.value;
    return (val != null && val !== '') ? val : defaultValue;
  }

  hasAnyFraudSignal(): boolean {
    return (
      this.getBSValue('BS Suspicious Dummy Name', '0')      === '1' ||
      this.getBSValue('BS Circular Txn Detected', '0')      === '1' ||
      this.getBSValue('BS UPI Circular Suspected', '0')     === '1' ||
      +this.getBSValue('BS Gambling Txn Count', '0')        > 0     ||
      +this.getBSValue('BS Crypto Txn Count', '0')          > 0     ||
      this.getBSValue('BS Artificial Balance Buildup', '0') === '1' ||
      +this.getBSValue('BS Frequent Loan App Txns', '0')    > 0
    );
  }

  getScoreColor(score: number, threshold: number): string {
    return score >= threshold ? 'var(--green)' : 'var(--rose)';
  }

  getScoreBg(score: number, threshold: number): string {
    return score >= threshold
      ? 'rgba(5,150,105,0.06)'
      : 'rgba(225,29,72,0.06)';
  }

  get decisionColor(): string {
    const flag = this.decisionFlag.toLowerCase();
    if (flag === 'approve' || flag === 'approved')      return 'approve';
    if (flag === 'conditional')                         return 'conditional';
    if (flag === 'decline' || flag === 'rejected')      return 'decline';
    return '';
  }

  proceed(): void {
    this.next.emit();
  }
}